import { getSpeechRecognitionCtor } from './voice-runtime';

export type VoiceInputCapabilityLevel = 'realtime' | 'upload_fallback' | 'unsupported';
export type VoiceBrowserFamily = 'chrome' | 'edge' | 'safari' | 'firefox' | 'unknown';
export type VoicePlatform = 'android' | 'ios' | 'desktop' | 'unknown';
export type VoiceRecommendedInteraction = 'native_live' | 'ptt_upload' | 'unsupported';
export type VoiceMatrixKey =
  | 'android_chrome_pwa'
  | 'android_chrome_browser'
  | 'android_edge_browser'
  | 'ios_safari_pwa'
  | 'ios_safari_browser'
  | 'desktop_chrome'
  | 'desktop_edge'
  | 'desktop_safari'
  | 'desktop_firefox'
  | 'unknown';

export interface VoiceInputCapability {
  level: VoiceInputCapabilityLevel;
  supportsRealtime: boolean;
  supportsNativeLive: boolean;
  supportsBrowserSpeechRecognition: boolean;
  supportsUploadFallback: boolean;
  supportsAlwaysOnCommands: boolean;
  supportsHighQualityTts: boolean;
  browserFamily: VoiceBrowserFamily;
  platform: VoicePlatform;
  recommendedInteraction: VoiceRecommendedInteraction;
  matrixKey: VoiceMatrixKey;
  isStandalonePwa: boolean;
  summary: string;
}

function detectPlatform(userAgent: string): VoicePlatform {
  if (/android/i.test(userAgent)) return 'android';
  if (/(iphone|ipad|ipod)/i.test(userAgent)) return 'ios';
  if (userAgent) return 'desktop';
  return 'unknown';
}

function detectBrowserFamily(userAgent: string): VoiceBrowserFamily {
  if (/edg\//i.test(userAgent)) return 'edge';
  if (/firefox\//i.test(userAgent)) return 'firefox';
  if (/chrome\//i.test(userAgent) && !/edg\//i.test(userAgent)) return 'chrome';
  if (/safari\//i.test(userAgent) && !/chrome\//i.test(userAgent) && !/chromium/i.test(userAgent)) return 'safari';
  return 'unknown';
}

function detectStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;
  const displayModeStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone =
    typeof navigator !== 'undefined' &&
    'standalone' in navigator &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayModeStandalone || iosStandalone;
}

function resolveMatrixKey(input: {
  browserFamily: VoiceBrowserFamily;
  platform: VoicePlatform;
  isStandalonePwa: boolean;
}): VoiceMatrixKey {
  const { browserFamily, platform, isStandalonePwa } = input;

  if (platform === 'android' && browserFamily === 'chrome') {
    return isStandalonePwa ? 'android_chrome_pwa' : 'android_chrome_browser';
  }
  if (platform === 'android' && browserFamily === 'edge') {
    return 'android_edge_browser';
  }
  if (platform === 'ios' && browserFamily === 'safari') {
    return isStandalonePwa ? 'ios_safari_pwa' : 'ios_safari_browser';
  }
  if (platform === 'desktop' && browserFamily === 'chrome') return 'desktop_chrome';
  if (platform === 'desktop' && browserFamily === 'edge') return 'desktop_edge';
  if (platform === 'desktop' && browserFamily === 'safari') return 'desktop_safari';
  if (platform === 'desktop' && browserFamily === 'firefox') return 'desktop_firefox';
  return 'unknown';
}

function buildCapabilitySummary(input: {
  supportsNativeLive: boolean;
  supportsUploadFallback: boolean;
  supportsAlwaysOnCommands: boolean;
  recommendedInteraction: VoiceRecommendedInteraction;
  browserFamily: VoiceBrowserFamily;
  platform: VoicePlatform;
}): string {
  const browserLabel = [input.platform, input.browserFamily].filter((part) => part !== 'unknown').join(' ');

  if (input.supportsNativeLive && input.supportsAlwaysOnCommands) {
    return `${browserLabel || 'This browser'} supports Gemini Live native voice and always-on commands.`;
  }
  if (input.supportsNativeLive) {
    return `${browserLabel || 'This browser'} supports native live voice, but product guardrails recommend push-to-talk.`;
  }
  if (input.supportsUploadFallback && input.recommendedInteraction === 'ptt_upload') {
    return `${browserLabel || 'This browser'} supports push-to-talk upload fallback only.`;
  }
  return 'Voice input unavailable in this browser.';
}

export function getVoiceInputCapability(): VoiceInputCapability {
  if (typeof window === 'undefined') {
    return {
      level: 'unsupported',
      supportsRealtime: false,
      supportsNativeLive: false,
      supportsBrowserSpeechRecognition: false,
      supportsUploadFallback: false,
      supportsAlwaysOnCommands: false,
      supportsHighQualityTts: false,
      browserFamily: 'unknown',
      platform: 'unknown',
      recommendedInteraction: 'unsupported',
      matrixKey: 'unknown',
      isStandalonePwa: false,
      summary: 'Voice input unavailable during server render.',
    };
  }

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const platform = detectPlatform(userAgent);
  const browserFamily = detectBrowserFamily(userAgent);
  const isStandalonePwa = detectStandalonePwa();
  const supportsBrowserSpeechRecognition = !!getSpeechRecognitionCtor();
  const supportsMicrophone =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;
  const supportsAudioContext =
    typeof window !== 'undefined' &&
    (typeof window.AudioContext !== 'undefined' ||
      typeof (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext !== 'undefined');
  const supportsWebSocket = typeof WebSocket !== 'undefined';
  const supportsNativeLive = supportsMicrophone && supportsAudioContext && supportsWebSocket;
  const supportsUploadFallback =
    supportsMicrophone &&
    typeof MediaRecorder !== 'undefined';
  const supportsHighQualityTts =
    typeof window !== 'undefined' &&
    (typeof Audio !== 'undefined' || supportsAudioContext);
  const supportsAlwaysOnCommands =
    supportsNativeLive &&
    platform !== 'ios' &&
    (platform === 'desktop' || browserFamily === 'chrome' || browserFamily === 'edge');
  const recommendedInteraction: VoiceRecommendedInteraction = supportsNativeLive
    ? 'native_live'
    : supportsUploadFallback
      ? 'ptt_upload'
      : 'unsupported';
  const matrixKey = resolveMatrixKey({ browserFamily, platform, isStandalonePwa });
  const supportsRealtime = supportsNativeLive;

  if (supportsNativeLive) {
    return {
      level: 'realtime',
      supportsRealtime: true,
      supportsNativeLive: true,
      supportsBrowserSpeechRecognition,
      supportsUploadFallback,
      supportsAlwaysOnCommands,
      supportsHighQualityTts,
      browserFamily,
      platform,
      recommendedInteraction,
      matrixKey,
      isStandalonePwa,
      summary: buildCapabilitySummary({
        supportsNativeLive: true,
        supportsUploadFallback,
        supportsAlwaysOnCommands,
        recommendedInteraction,
        browserFamily,
        platform,
      }),
    };
  }

  if (supportsUploadFallback) {
    return {
      level: 'upload_fallback',
      supportsRealtime: false,
      supportsNativeLive: false,
      supportsBrowserSpeechRecognition,
      supportsUploadFallback: true,
      supportsAlwaysOnCommands: false,
      supportsHighQualityTts,
      browserFamily,
      platform,
      recommendedInteraction,
      matrixKey,
      isStandalonePwa,
      summary: buildCapabilitySummary({
        supportsNativeLive: false,
        supportsUploadFallback: true,
        supportsAlwaysOnCommands: false,
        recommendedInteraction,
        browserFamily,
        platform,
      }),
    };
  }

  return {
    level: 'unsupported',
    supportsRealtime: false,
    supportsNativeLive: false,
    supportsBrowserSpeechRecognition,
    supportsUploadFallback: false,
    supportsAlwaysOnCommands: false,
    supportsHighQualityTts: false,
    browserFamily,
    platform,
    recommendedInteraction: 'unsupported',
    matrixKey,
    isStandalonePwa,
    summary: buildCapabilitySummary({
      supportsNativeLive: false,
      supportsUploadFallback: false,
      supportsAlwaysOnCommands: false,
      recommendedInteraction: 'unsupported',
      browserFamily,
      platform,
    }),
  };
}
